import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { EditableContent, EditableContentComponent } from 'components/input/editable-content/editable-content.component';
import { ListRowComponent } from 'components/layout/list-view/list-view.component';
import { OrderProductPlanDiscontinuationModel } from 'models/generated/Entities.GormCrm.OrderProductPlanDiscontinuation';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { Pipes } from 'modules/pipes';
import { CompanyDataProviderFactory } from 'services/company-data-provider';
import { EditPlanDiscontinuationComponent } from './edit-plan-discontinuation.component';
import { LedgerPostType } from 'enums/LedgerPostType';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { InputType } from 'enums/InputType';
import { AccountingService } from 'services/accounting.service';
import { NgTemplateOutlet } from '@angular/common';

@Component({
	selector: 'order-plan-discontinuations',
	templateUrl: 'order-plan-discontinuations.component.html',
	providers: [CompanyDataProviderFactory], // Provide the factory to the component, but do not request an instance until edit mode is activated, to prevent sending too many requests when viewing many orders
	imports: [Layout, Pipes, ListView, EditableContent, EditPlanDiscontinuationComponent, CustomInputComponent, NgTemplateOutlet]
})
export class OrderPlanDiscontinuationsComponent {

	private _editMode = false;
	private _discontinuations: OrderProductPlanDiscontinuationModel[] = [];
	public hasMigratedInvoices = false;
	public inputType = InputType;

	@Input() public companyId?: string;
	@Input() public set discontinuations(discontinuations: OrderProductPlanDiscontinuationModel[]) {
		this._discontinuations = discontinuations;
		// Remember the originally selected product plans, to ensure they are still selectable even though they are suspended
		this.allowedProductPlans = discontinuations.filter(d => d.productPlanId).map(d => d.productPlanId!);
	}
	public get discontinuations() { return this._discontinuations; }
	@Input() public set editMode(editMode: boolean) {
		this._editMode = editMode;
		if (editMode && this.companyId) {
			// Preload product plan information to avoid having to wait for it when editing
			const dataProvider = this.dataProviderFactory.getProviderForCompanyId(this.companyId);
			dataProvider.getSupersedableProductPlans(true); // Enforce reloaded cache, because saving orders can alter which product plans are suspended
			dataProvider.getAllProductPlansAndAccountingPlans();
			
			this.checkForMigratedLedgerPosts();
		}
	}
	public get editMode() { return this._editMode; }
	public editorOffsetPosition?: ElementRef<HTMLElement>;
	public allowedProductPlans: string[] = [];
	public totalRemainingAmount = 0;
	public totalAdjustmentAmount = 0;
	public totalDiscontinuationAccountingPlanValue = 0;

	@Output() private discontinuationChanged = new EventEmitter();
	@ViewChild('editor') private editor?: EditableContentComponent;
	@ViewChild('editorManualValues') private editorManualValues?: EditableContentComponent;
	private editDiscontinuation?: OrderProductPlanDiscontinuationModel;

	constructor(private readonly dataProviderFactory: CompanyDataProviderFactory, private readonly accountingService: AccountingService) { }

	edit(object: OrderProductPlanDiscontinuationModel, row?: ListRowComponent) {
		if (!this.editMode || !this.editor) return;
		this.editDiscontinuation = object;
		this.editorOffsetPosition = row?.getRowElement();
		setTimeout(() => this.editor?.beginEdit(object));
	}

	updateDiscontinuation(updatedObject: OrderProductPlanDiscontinuationModel) {
		if (!this.editDiscontinuation) return;
		if (this.discontinuations.indexOf(this.editDiscontinuation) < 0) this.discontinuations.push(this.editDiscontinuation);

		Object.assign(this.editDiscontinuation, updatedObject);
		this.checkForMigratedLedgerPosts();
		this.discontinuationChanged.emit();
	}

	removeEditingDiscontinuation() {
		if (!this.editDiscontinuation) return;
		const index = this.discontinuations.indexOf(this.editDiscontinuation);
		if (index >= 0) this.discontinuations.splice(index, 1);
		this.editor?.cancelEdit();
		this.checkForMigratedLedgerPosts();
		this.discontinuationChanged.emit();
	}

	addNewDiscontinuation() {
		this.edit(new OrderProductPlanDiscontinuationModel());
	}

	async checkForMigratedLedgerPosts() {
		const dataProvider = this.dataProviderFactory.getProviderForCompanyId(this.companyId!);
		const ledgerPosts = await dataProvider.getLedgerPostsByProductPlanIds(this.discontinuations.map(d => d.productPlanId!).filter(id => !!id));
		const productPlans = await dataProvider.getAllProductPlansAndAccountingPlans();
		for (const discontinuation of this.discontinuations) {
			discontinuation.productPlan = productPlans.find(plan => plan.id === discontinuation.productPlanId);
		}
		this.hasMigratedInvoices = ledgerPosts.some(post => post.type === LedgerPostType.MigratedInvoice);
	}

	manuallyEditRemainingAndCounterAdjustment() {
		if (!this.editMode || !this.editorManualValues) return;
		this.totalAdjustmentAmount = this.discontinuations.reduce((sum, d) => sum + (d.adjustmentAmount ?? 0), 0);
		this.totalRemainingAmount = this.discontinuations.reduce((sum, d) => sum + (d.remainingAmount ?? 0), 0);
		this.totalDiscontinuationAccountingPlanValue = this.discontinuations.reduce((sum, d) => sum + (this.accountingService.filterCurrentAccountingPlan(d.productPlan?.accountingPlans).totalPlanValue ?? 0), 0);
		this.editorManualValues?.beginEdit(this.discontinuations);
	}

	redistributeAmounts(discontinuations: OrderProductPlanDiscontinuationModel[], updatedValue: number, type: 'remaining' | 'adjustment') {
		if (type === 'remaining')
			this.totalRemainingAmount = updatedValue;
		else if (type === 'adjustment')
			this.totalAdjustmentAmount = updatedValue;

		let lastRelevantDiscontinuation;
		for (let discontinuation of discontinuations) {
			if (!discontinuation.productPlan) continue;
			const activeAccountingPlan = this.accountingService.filterCurrentAccountingPlan(discontinuation.productPlan.accountingPlans);
			if (!activeAccountingPlan) continue;
			const proportion = this.totalDiscontinuationAccountingPlanValue > 0 ? activeAccountingPlan.totalPlanValue / this.totalDiscontinuationAccountingPlanValue : 0;
			const amount = (updatedValue * proportion).toDecimals(2);
			if (type === 'remaining')
				discontinuation.remainingAmount = amount;
			else if (type === 'adjustment')
				discontinuation.adjustmentAmount = amount;
			
			if (amount > 0) lastRelevantDiscontinuation = discontinuation;
		}
		const totalAmount = discontinuations
			.reduce((sum, d) => sum + ((
				type === 'remaining' ? d.remainingAmount : type === 'adjustment' ? d.adjustmentAmount : 0
			) ?? 0), 0);
		if (type === 'remaining' && totalAmount != this.totalRemainingAmount && lastRelevantDiscontinuation)
			lastRelevantDiscontinuation.remainingAmount += (this.totalRemainingAmount - totalAmount).toDecimals(2);
		if (type === 'adjustment' && totalAmount != this.totalAdjustmentAmount && lastRelevantDiscontinuation)
			lastRelevantDiscontinuation.adjustmentAmount += (this.totalAdjustmentAmount - totalAmount).toDecimals(2);
	}

	get totalPlanValueGreaterThanRemainingAndAdjustment() {
		return this.totalDiscontinuationAccountingPlanValue > this.totalRemainingAmount + this.totalAdjustmentAmount;
	}
	
	updateDiscontinuationsManually(updatedObjects: OrderProductPlanDiscontinuationModel[]) {
		Object.assign(this.discontinuations, updatedObjects);
		this.discontinuationChanged.emit();
	}
}
